import { LitElement, css, html, nothing } from '@lit'
import styles from '@nova/styles'

class NovaSearchResult extends LitElement {
  static properties = {
    searchData: { type: Object },
    searchTerm: { type: String },
    noSearchResult: { type: Boolean },
    suggestedResult: { type: Object },
    openState: { type: String },
  }

  connectedCallback() {
    super.connectedCallback()
    this.searchData = {}
    this.searchTerm = ''
    this.noSearchResult = false
    this.suggestedResult = null

    window.addEventListener('resize', this.handleRenderHeight.bind(this))
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    window.removeEventListener('resize', this.handleRenderHeight.bind(this))
  }

  updated(changedProperties) {
    super.updated(changedProperties)
    if (this.openState === 'closed') return
    if (Reflect.ownKeys(this.searchData).length === 0) return
    const { products } = this.searchData.resources.results
    this.noSearchResult = products.length === 0

    if (this.suggestedResult === null && this.noSearchResult) {
      this.handleResult()
    } else if (this.noSearchResult) {
      this.handleRenderResult(this.suggestedResult)
    }

    this.handleRenderHeight()
  }

  async handleResult() {
    try {
      const response = await fetch(
        `?section_id=${window.main_suggested_result_section_id}`,
      )
      if (!response.ok) {
        console.error(`HTTP error! Status: ${response.status}`)
        console.error(`HTTP error! Reason: ${response}`)
      }
      const data = await response.text()
      const parser = new DOMParser()
      const htmlDocument = parser.parseFromString(data, 'text/html')
      this.suggestedResult = htmlDocument
      this.handleRenderResult(this.suggestedResult)
    } catch (error) {
      console.error('Error:', error.message)
    }
  }

  handleRenderHeight() {
    if (this.openState === 'closed') return

    clearTimeout(this.debounceTimeout)
    this.debounceTimeout = setTimeout(() => {
      const resultRec = this.getBoundingClientRect()
      if (window.innerHeight - resultRec.bottom > 0) {
        this.style.maxHeight = 'unset'
      } else {
        this.style.maxHeight = `${window.innerHeight - resultRec.top}px`
      }
    }, 500)
  }

  handleRenderResult(data) {
    const suggestedResult = data.querySelector('.suggested-results')
    if (!suggestedResult) return

    const productContainer = suggestedResult.querySelector(
      '.header-search__product-container',
    )
    const currentProductContainer = this.shadowRoot.querySelector(
      '.header-search__product-container',
    )
    if (productContainer && currentProductContainer) {
      currentProductContainer.replaceWith(productContainer.cloneNode(true))
      this.assignProductCardProperties()
    }
  }

  assignProductCardProperties() {
    const productCardContainers = this.shadowRoot.querySelectorAll(
      '.js-suggested-product-card',
    )

    const getValuesFromTemplate = (productId, type) => {
      const template = this.shadowRoot.querySelector(
        `#suggested-product__${type}-json-${productId}`,
      )
      return template ? JSON.parse(template.content.textContent.trim()) : []
    }

    for (const productContainer of productCardContainers) {
      const productId = productContainer.dataset.id
      const swatchValues = getValuesFromTemplate(productId, 'swatch-values')
      const sizeValues = getValuesFromTemplate(productId, 'size-values')
      const variants = getValuesFromTemplate(productId, 'variants')
      const productCard = productContainer.querySelector('nova-product-card')

      productCard.swatches = swatchValues
      productCard.sizes = sizeValues
      productCard.variants = variants
      productCard.mode = 'predictive-search'
    }
  }

  render() {
    if (Reflect.ownKeys(this.searchData).length === 0) return nothing
    if (this.noSearchResult) {
      return html`
        <div class="header-search__result">
          <div class="header-search__no-result-container header-search__mobile-padding">
            <div class="header-search__no-result-content">
              <h3 class="header-search__no-result-title">
                ${window.searchStrings.no_results_found_for} "${this.searchTerm}"
              </h3>
              <p class="header-search__no-result-description">
                ${window.searchStrings.search_again}
              </p>  
            </div>
          </div>
          <div class="header-search__product-container"></div>
        </div>
      `
    }
    const displayProducts = this.searchData.resources.results.products.slice(
      0,
      5,
    )

    return html`
        <div class="header-search__result">
          ${
            this.searchData.resources.results.products.length > 0
              ? html` <div class="header-search__product-container">
                <h3 class="header-search__mobile-padding">Products</h3>
                <div class="header-search__product-slide-container">
                  <div class="header-search__product-slide-inner-container">
                    ${displayProducts.map((product) => {
                      return html`<nova-search-product 
                        .product=${product}
                        .variants=${product.variants}
                        ></nova-search-product>`
                    })}
                  </div>
                </div>
              </div>`
              : nothing
          }
        </div>
      `
  }

  static styles = [
    styles.variables,
    css`
    :host .header-search__result {
      display: flex;
      overflow: hidden;
      flex-direction: column;
      max-height: inherit;
      overflow-y: auto;
      -ms-overflow-style: none;
      scrollbar-width: none;
      color: var(--nova-brand-ink);
      gap: var(--nova-space-md);
    }

    :host .header-search__result::-webkit-scrollbar {
      display: none;
      width: 0;
    }

    :host .header-search__product-container {
      width: 100%;
    }

    @media screen and (min-width: 990px) {
      :host nova-search-product:first-of-type {
        padding-left: 0;
      }

      :host nova-search-product:last-of-type {
        padding-right:0;
      }

      :host .header-search__product-card:first-of-type {
        padding-left: 0;
      }
      :host .header-search__product-card:last-of-type {
        padding-right: 0;
      }
    }

    :host .header-search__list-container {
      display: flex;
      flex-direction: column;
      padding: 0;
      width: 100%;
      gap: var(--nova-space-md);
    }

    @media screen and (min-width: 990px) {
      :host .header-search__result {
        padding: 0 var(--nova-space-page-margin) var(--nova-space-sm);
      }
    }

    :host .header-search__product-slide-container {
      overflow-x: auto;
      width: 100%;
      max-width: 100%;
      -ms-overflow-style: none; /* Hide scrollbar for IE and Edge */
      scrollbar-width: none; /* Hide scrollbar for Firefox */
    }

    /* Hide scrollbar for Chrome, Safari and Opera */
    :host .header-search__product-slide-container::-webkit-scrollbar {
      display: none;
      width: 0;
    }

    :host h3 {
      font: var(--nova-h6-font);
      line-height: 150%;
      font-weight: 500;
      margin-top: 0;
    }


    :host a {
      color: inherit;
      text-decoration: none;
    }

    :host ul {
      display: flex;
      flex-direction: column;
      gap: var(--nova-space-xs);
      padding-left: var(--nova-space-none);
      margin: 0 var(--nova-space-page-margin);
    }

    @media screen and (min-width: 990px) {
      :host ul {
        margin: 0;
      }
    }

    :host li {
      list-style: none;
      color: var(--nova-color-subdued);
    }

    :host a:focus-visible {
      border: 2px solid var(--nova-utility-focus);
    }

    :host .header-search__no-result-title {
      font: var(--nova-h5-font);
      text-transform: Capitalize;
      word-break: break-all;
    }

    :host .header-search__no-result-description {
      color: var(--nova-color-subdued);
    }

    @media screen and (min-width: 990px) {
      :host .header-search__no-result-description {
        margin: 0;
      }
    }

    @media screen and (max-width: 989px) {
      :host .header-search__mobile-padding, 
      :host .header-search__product-container h3 {
        padding: 0 var(--nova-space-page-margin) 0;
      }
    }

    :host .header-search__no-result-container {
      display: flex;
      flex-direction: column;
      gap: var(--nova-space-md);
    }

    :host .header-search__product-slide-inner-container {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      width: 100%;
      gap: var(--nova-space-xs);
    }

    :host .header-search__product-slide-inner-container > nova-search-product,
    :host .header-search__product-slide-inner-container > .header-search__product-card {
      display: block;
      width: 41%;
      flex-shrink: 0;
    }

    :host .header-search__product-slide-inner-container > nova-search-product:first-of-type,
    :host .header-search__product-slide-inner-container > .header-search__product-card:first-of-type {
      padding-left: var(--nova-space-page-margin);
    }

    :host .header-search__product-slide-inner-container > nova-search-product:last-of-type,
    :host .header-search__product-slide-inner-container > .header-search__product-card:last-of-type {
      padding-right: var(--nova-space-page-margin);
    }

    @media screen and (min-width: 990px) {
      :host .header-search__product-slide-inner-container > nova-search-product:first-of-type,
      :host .header-search__product-slide-inner-container > .header-search__product-card:first-of-type {
        padding-left: 0;
      }

      :host .header-search__product-slide-inner-container > nova-search-product:last-of-type,
      :host .header-search__product-slide-inner-container > .header-search__product-card:last-of-type {
        padding-right: 0;
      }
    }

    @media screen and (min-width: 1024px) {
      :host .header-search__product-slide-inner-container:has(nova-search-product) {
        gap: var(--nova-space-md);
      }
      :host .header-search__product-slide-inner-container:has(header-search__product-card) {
        gap: var(--nova-space-sm);
      }

      :host .header-search__product-slide-inner-container > nova-search-product,
      :host .header-search__product-slide-inner-container > .header-search__product-card {
        width: 28%;
      }
    }

    @media screen and (min-width: 1420px) {
      :host .header-search__product-slide-inner-container:has(nova-search-product) {
        gap: var(--nova-space-lg);
      }

      :host .header-search__product-slide-inner-container > nova-search-product:nth-of-type(-n+5) {
        display: block;
        width: 20%;
      }
    }

    @media screen and (min-width: 1600px) {
      :host .header-search__product-slide-inner-container:has(nova-search-product) {
        gap: var(--nova-space-2xl);
      }
    }


    :host .header-search__product-card img {
      width: 100%;
      aspect-ratio: 160 / 214;
      object-fit: cover;
      object-position: center;
    }

    :host h4 {
      font: var(--nova-h6-font);
      margin: var(--nova-space-none);
    }

    :host p {
      margin: var(--nova-space-none);
    }

    :host .header-search__product-compared-price {
      color: var(--nova-color-subdued);
      text-decoration: line-through;
    }

    @media screen and (min-width: 1300px) {
      :host h4 {
        font-size: 18px;
      }
    }
  `,
  ]
}

customElements.define('nova-search-result', NovaSearchResult)
